import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPrescriptionComplaints } from "@/services/prescriptionCompliance";
import { handleCatch } from "@/shared/helpers";
import {
  resolvePrescriptionComplaintUserKey,
  resolveComplianceIndicator,
} from "../utils/prescriptionComplianceHelpers";

/**
 * Loads non-compliance complaints when the tab is open.
 * Compliance for the current PAF is read from `form` / `receivedForm` only
 * (no per-prescription fetches for history list items).
 */
export function usePrescriptionCompliance({
  patientDetails,
  form,
  receivedForm,
  loadComplaints = false,
}) {
  const complaintUserKey = useMemo(
    () => resolvePrescriptionComplaintUserKey(patientDetails, form, receivedForm),
    [patientDetails, form, receivedForm]
  );

  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [indicatorsByPrescriptionId, setIndicatorsByPrescriptionId] = useState(
    {}
  );

  useEffect(() => {
    if (!loadComplaints || !complaintUserKey) {
      return;
    }

    let cancelled = false;
    setComplaintsLoading(true);

    fetchPrescriptionComplaints(complaintUserKey)
      .then((list) => {
        if (!cancelled) setComplaints(list);
      })
      .catch((error) => {
        if (!cancelled) {
          handleCatch(error);
          setComplaints([]);
        }
      })
      .finally(() => {
        if (!cancelled) setComplaintsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadComplaints, complaintUserKey]);

  useEffect(() => {
    const id = form?.id;
    if (id == null) return;
    const indicator = resolveComplianceIndicator(form);
    if (indicator !== true && indicator !== false) return;
    setIndicatorsByPrescriptionId((prev) => ({
      ...prev,
      [String(id)]: indicator,
    }));
  }, [form?.id, form?.compliance_indicator, form?.status, form?.status_name, form]);

  const getIndicator = useCallback(
    (prescriptionId) => {
      if (prescriptionId == null) return undefined;
      const key = String(prescriptionId);
      const mapped = indicatorsByPrescriptionId[key];
      if (mapped === true || mapped === false) return mapped;

      if (String(form?.id) === key) {
        const fromForm = resolveComplianceIndicator(form);
        if (fromForm !== undefined) return fromForm;
      }

      if (String(receivedForm?.id) === key) {
        const fromReceived = resolveComplianceIndicator(receivedForm);
        if (fromReceived !== undefined) return fromReceived;
      }

      return undefined;
    },
    [indicatorsByPrescriptionId, form, receivedForm]
  );

  return {
    complaintUserKey,
    complaints,
    complaintsLoading,
    indicatorsByPrescriptionId,
    getIndicator,
    loading: complaintsLoading,
  };
}
